import { stripSignature } from "@redotech/email/signature";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { uploadFile } from "@redotech/redo-api-client/conversations";
import type { AutoReplyAction as AutoReplyActionType } from "@redotech/redo-flows-common/rules/schemas";
import EditIcon from "@redotech/redo-icons/edit-05.svg";
import InfoSvg from "@redotech/redo-icons/info-circle.svg";
import PaperclipIcon from "@redotech/redo-icons/paperclip.svg";
import XCloseIcon from "@redotech/redo-icons/x-close.svg";
import { conversationFileUploadErrorMessages } from "@redotech/redo-support-common/support/conversations/conversation-file-upload-error";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Tooltip } from "@redotech/redo-web/arbiter-components/tooltip/tooltip";
import { AttachmentThumbnail } from "@redotech/redo-web/attachment-thumbnail";
import { RedoClientContext } from "@redotech/redo-web/client";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { nativeRandom, randomHexString } from "@redotech/util/random";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { useVariableAutocomplete } from "../../hooks/use-variable-autocomplete";
import { MacroEditorToolbar } from "../../setting/macros/macro-editor-toolbar";
import { MessageEditor } from "../message-editor/message-editor";
import {
  htmlToPlateContent,
  useMessageEditor,
} from "../message-editor/use-message-editor";
import { VariableAutocomplete } from "../variable-autocomplete";
import { VariableInsertButton } from "../variable-insert-button";
import * as css from "./auto-reply-action.module.css";
import { buildSeededAutoReplyHtml } from "./auto-reply-signature";

const SIGNATURE_FIELDS = ["textContent", "htmlContent", "attachments"] as const;

/** Plate-based auto-reply rule action for support-v2. Self-contained here so the
 * legacy Quill component can be deleted with support-v1 without shared artifacts. */
export const SupportV2AutoReplyAction = memo(function SupportV2AutoReplyAction({
  action,
  onChange,
  editableFields,
}: {
  action: AutoReplyActionType;
  onChange: (updated: AutoReplyActionType) => void;
  editableFields?: ReadonlySet<string>;
}) {
  // Any auto-reply field being bound unlocks the editor; none bound locks it.
  const isLocked =
    editableFields !== undefined &&
    !SIGNATURE_FIELDS.some((f) => editableFields.has(f));

  const team = useRequiredContext(TeamContext);
  const apiClient = useRequiredContext(RedoClientContext);
  const [showPlaintext, setShowPlaintext] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const editorContainerRef = useRef<HTMLDivElement | null>(null);
  const editorId = useRef(`auto-reply-${randomHexString(nativeRandom, 8)}`);

  // Mirror the editor's signature seeding so the drawer preview matches the
  // editor even before the reply has been edited.
  const previewHtml = buildSeededAutoReplyHtml(
    action.htmlContent,
    team?.settings?.support?.emailSignature,
    action.signatureSeeded,
  );

  // Seed once from initial props; later edits flow through onChange.
  const initialValue = useMemo(() => {
    const seededHtml = buildSeededAutoReplyHtml(
      action.htmlContent,
      team?.settings?.support?.emailSignature,
      action.signatureSeeded,
    );
    return seededHtml.length > 0 ? htmlToPlateContent(seededHtml) : undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist the seed once so deleting the signature in the editor sticks
  // instead of being re-appended on the next render or remount.
  useEffect(() => {
    const signature = team?.settings?.support?.emailSignature;
    if (action.signatureSeeded || !signature) {
      return;
    }
    onChange({
      ...action,
      htmlContent: buildSeededAutoReplyHtml(action.htmlContent, signature),
      signatureSeeded: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const editorState = useMessageEditor("email", editorId.current, initialValue);

  const variableAutocomplete = useVariableAutocomplete({
    editorState,
    enabled: !isLocked,
    containerRef: editorContainerRef,
  });

  const handleMessage = useHandler(async () => {
    if (isLocked) {
      return;
    }
    const isEmpty =
      editorState.getPlainText().trim().length === 0 &&
      !editorState.hasInlineImages();
    const textContent = isEmpty ? "" : editorState.getPlainText();
    const htmlContent = isEmpty ? "" : await editorState.getHtml();

    if (
      neuterImgTags(htmlContent) !== neuterImgTags(action.htmlContent) ||
      textContent !== action.textContent
    ) {
      onChange({ ...action, textContent, htmlContent });
    }
  });

  const handleUpload = useHandler(async (files: FileList | null) => {
    if (isLocked || !files || files.length === 0) {
      return;
    }
    setUploading(true);
    try {
      const uploaded: { url: string; description: string; mimeType: string }[] =
        [];
      for (const file of Array.from(files)) {
        const form = new FormData();
        form.append("file", file);
        form.append("fileName", file.name);
        const response = await uploadFile(apiClient, form);
        if (response.success) {
          const { url, description, mimeType } = response.body;
          uploaded.push({ url, description, mimeType });
        } else {
          toast(
            conversationFileUploadErrorMessages?.[response.error] ??
              "Unknown error uploading file.",
            { variant: "error" },
          );
        }
      }
      if (uploaded.length > 0) {
        onChange({
          ...action,
          attachments: [...(action.attachments || []), ...uploaded],
        });
      }
    } finally {
      setUploading(false);
    }
  });

  // Persist on close so a freshly-seeded reply (signature added but never typed
  // into) is saved, then drop out of edit mode.
  const closeEditor = useHandler(() => {
    void handleMessage();
    setIsEditing(false);
  });

  const handleAttachmentRemoval = useHandler((url: string) => {
    if (isLocked) {
      return;
    }
    onChange({
      ...action,
      attachments: (action.attachments || []).filter((a) => a.url !== url),
    });
  });

  return (
    <Flex
      dir="column"
      gap="sm"
      pointerEvents={isLocked ? "none" : "auto"}
      style={isLocked ? { opacity: 0.6 } : undefined}
      w="full"
    >
      <Flex align="center" justify="space-between" w="full">
        <Text>Message to customer</Text>
        <RedoButton
          disabled={isLocked}
          hierarchy="tertiary"
          IconLeading={EditIcon}
          onClick={() => setIsEditing(true)}
          size="sm"
          text="Edit"
        />
      </Flex>

      {previewHtml.trim().length > 0 ? (
        <div
          className={css.htmlPreview}
          dangerouslySetInnerHTML={{ __html: previewHtml }}
        />
      ) : (
        <Text fontSize="sm" textColor="secondary">
          No reply message yet. Click Edit to write one.
        </Text>
      )}

      {action.attachments && action.attachments.length > 0 && (
        <Flex dir="column" gap="sm" overflowX="auto">
          {action.attachments.map((attachment) => (
            <AttachmentThumbnail
              description={attachment.description}
              key={attachment.url}
              mimeType={attachment.mimeType}
              restrictWidth
              url={attachment.url}
            />
          ))}
        </Flex>
      )}

      {isEditing && (
        <RedoModal
          contentScrollable
          modalSize="lg"
          onModalCloseRequested={closeEditor}
          primaryButton={{ text: "Done", onClick: closeEditor }}
          title="Auto-reply message"
        >
          <Flex dir="column" gap="md" w="full">
            <Flex align="center" gap="xs" justify="flex-end" w="full">
              <Tooltip
                placement="bottom-start"
                title="The formatted version of the message will be used when replying to email messages. When responding to other message types such as Facebook or SMS, the plaintext message will be sent."
              >
                <Flex align="center" cursor="pointer">
                  <InfoSvg />
                </Flex>
              </Tooltip>
              <Text fontSize="xs" textColor="secondary">
                Show plaintext
              </Text>
              <RedoToggle
                setValue={setShowPlaintext}
                size="sm"
                value={showPlaintext}
              />
            </Flex>

            {showPlaintext ? (
              <Text
                bgColor="tertiary"
                borderRadius="sm"
                fontSize="sm"
                p="lg"
                whiteSpace="pre-wrap"
              >
                {stripSignature(action.textContent)}
              </Text>
            ) : (
              <Flex dir="column" ref={editorContainerRef} w="full">
                <MessageEditor
                  actions={
                    <Flex align="center" gap="sm">
                      <VariableInsertButton
                        disabled={variableAutocomplete.isOpen}
                        editorState={editorState}
                      />
                      <RedoButton
                        disabled={uploading}
                        hierarchy="tertiary"
                        IconLeading={PaperclipIcon}
                        onClick={() => fileInputRef.current?.click()}
                        size="sm"
                        text="Attach file"
                      />
                    </Flex>
                  }
                  disabled={isLocked}
                  editorState={editorState}
                  onChange={() => {
                    variableAutocomplete.handleEditorChange();
                    void handleMessage();
                  }}
                  placeholder="Type your reply. Type / or use Insert variable to add a merge field."
                  toolbar={<MacroEditorToolbar />}
                />
                <VariableAutocomplete
                  anchorRect={variableAutocomplete.anchorRect}
                  focusedIndex={variableAutocomplete.focusedIndex}
                  onClose={variableAutocomplete.close}
                  onSelect={variableAutocomplete.handleSelectVariable}
                  open={variableAutocomplete.isOpen}
                  setFocusedIndex={variableAutocomplete.setFocusedIndex}
                  variables={variableAutocomplete.filteredVariables}
                />
              </Flex>
            )}

            <input
              hidden
              multiple
              onChange={(e) => {
                void handleUpload(e.target.files);
                e.target.value = "";
              }}
              ref={fileInputRef}
              type="file"
            />

            {action.attachments && action.attachments.length > 0 && (
              <Flex dir="column" gap="sm" overflowX="auto">
                {action.attachments.map((attachment) => (
                  <Flex align="center" gap="sm" key={attachment.url}>
                    <AttachmentThumbnail
                      description={attachment.description}
                      mimeType={attachment.mimeType}
                      restrictWidth
                      url={attachment.url}
                    />
                    <RedoButton
                      aria-label="Remove attachment"
                      hierarchy="tertiary"
                      IconLeading={XCloseIcon}
                      onClick={() => handleAttachmentRemoval(attachment.url)}
                      size="xs"
                      theme="destructive"
                    />
                  </Flex>
                ))}
              </Flex>
            )}
          </Flex>
        </RedoModal>
      )}
    </Flex>
  );
});

const neuterImgTags = (htmlContent: string) => {
  const imgRegex = /<img.*?>/g;
  return htmlContent.replace(imgRegex, "<img />");
};
